import './Questions.css';
import '../../Dashboard.css';
import SERVER_URL from '../../../../env.js';
import { useEffect, useState, useCallback, useMemo } from 'react';
import Cookies from 'js-cookie';

function Questions({ currentClientID, currentClientName }) {
    const [clientQuestions, setClientQuestions] = useState([]);
    const [searchClientQ, setSearchClientQ] = useState('');
    const [searchClientTopic, setSearchClientTopic] = useState('');
    const [searchClientYear, setSearchClientYear] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Master questions state (for adding)
    const [masterQuestions, setMasterQuestions] = useState([]);
    const [masterLoading, setMasterLoading] = useState(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingCQId, setEditingCQId] = useState(null); // The client_question ID if editing

    // Add modal search & selection
    const [searchQ, setSearchQ] = useState('');
    const [searchTopic, setSearchTopic] = useState('');
    const [selectedMasterQ, setSelectedMasterQ] = useState(null);
    const [yearInput, setYearInput] = useState('');
    const [searchClientAnswered, setSearchClientAnswered] = useState('');

    // Form state
    const [hasAnswer, setHasAnswer] = useState(false);
    const [answerText, setAnswerText] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [deleting, setDeleting] = useState(false);

    const fetchClientQuestions = useCallback(() => {
        if (!currentClientID) return;
        setLoading(true);
        setError('');
        fetch(`${SERVER_URL}/client_questions/${currentClientID}`, {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
        })
            .then(async (res) => {
                if (!res.ok) {
                    const d = await res.json().catch(() => ({}));
                    throw new Error(d.error || `HTTP ${res.status}`);
                }
                return res.json();
            })
            .then((data) => setClientQuestions(data.clientQuestions || []))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [currentClientID]);

    const fetchMasterQuestions = useCallback(() => {
        if (masterQuestions.length > 0) return; // Already fetched
        setMasterLoading(true);
        fetch(`${SERVER_URL}/questions`, {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
        })
            .then(res => res.json())
            .then(data => setMasterQuestions(data.questions || []))
            .catch(err => console.error(err))
            .finally(() => setMasterLoading(false));
    }, [masterQuestions.length]);

    useEffect(() => { fetchClientQuestions(); }, [fetchClientQuestions]);


    /* ── Modal Helpers ── */
    const openAddModal = () => {
        setIsModalOpen(true);
        setIsEditing(false);
        setEditingCQId(null);
        setSelectedMasterQ(null);
        setYearInput('');
        setHasAnswer(false);
        setAnswerText('');
        setSaveError('');
        fetchMasterQuestions();
    };

    const openEditModal = (cq) => {
        setIsModalOpen(true);
        setIsEditing(true);
        setEditingCQId(cq.id);
        // We pretend selectedMasterQ is the one from the client_question to show its text
        setSelectedMasterQ({
            id: cq.question_id,
            question: cq.question,
            topic: cq.topic,
        });
        setYearInput(cq.year !== null && cq.year !== undefined ? String(cq.year) : '');
        setHasAnswer(cq.has_answer);
        setAnswerText(cq.answer || '');
        setSaveError('');
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedMasterQ(null);
        setSearchQ('');
        setSearchTopic('');
        setYearInput('');
    };

    /* ── Filtered Client Questions ── */
    const filteredClientQuestions = useCallback(() => {
        return clientQuestions.filter(cq => {
            if (searchClientQ && !(cq.question || '').toLowerCase().includes(searchClientQ.toLowerCase())) return false;
            if (searchClientTopic && !(cq.topic || '').toLowerCase().includes(searchClientTopic.toLowerCase())) return false;
            if (searchClientYear && !(String(cq.year) || '').includes(searchClientYear)) return false;
            if (searchClientAnswered && !(String(cq.has_answer) || '').includes(searchClientAnswered)) return false;
            return true;
        });
    }, [clientQuestions, searchClientQ, searchClientTopic, searchClientYear, searchClientAnswered]);

    const filteredMasterQuestions = useCallback(() => {
        return masterQuestions.filter(q => {
            if (searchQ && !(q.question || '').toLowerCase().includes(searchQ.toLowerCase())) return false;
            if (searchTopic && !(q.topic || '').toLowerCase().includes(searchTopic.toLowerCase())) return false;
            return true;
        });
    }, [masterQuestions, searchQ, searchTopic]);

    /* ── API Actions ── */
    const handleSave = async () => {
        if (!selectedMasterQ && !isEditing) {
            setSaveError('Please select a question to add.');
            return;
        }

        setSaving(true);
        setSaveError('');

        const payload = {
            has_answer: hasAnswer,
            answer: answerText,
            year: yearInput ? parseInt(yearInput, 10) : null,
        };

        let url = `${SERVER_URL}/client_questions/${currentClientID}`;
        let method = 'POST';

        if (isEditing) {
            url = `${SERVER_URL}/client_questions/${editingCQId}`;
            method = 'PUT';
        } else {
            payload.question_id = selectedMasterQ.id;
        }

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) {
                setSaveError(data.error || 'Failed to save.');
            } else {
                if (isEditing) {
                    setClientQuestions(prev => prev.map(c => c.id === editingCQId ? data.clientQuestion : c));
                } else {
                    setClientQuestions(prev => [data.clientQuestion, ...prev]);
                }
                closeModal();
            }
        } catch (e) {
            setSaveError('An unexpected error occurred.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Remove this question from the client?')) return;
        setDeleting(true);
        setSaveError('');

        try {
            const res = await fetch(`${SERVER_URL}/client_questions/${editingCQId}`, {
                method: 'DELETE',
                headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setSaveError(data.error || 'Failed to delete.');
            } else {
                setClientQuestions(prev => prev.filter(c => c.id !== editingCQId));
                closeModal();
            }
        } catch (e) {
            setSaveError('An unexpected error occurred.');
        } finally {
            setDeleting(false);
        }
    };

    if (!currentClientID) {
        return (
            <div className="cq-empty">
                <p>No Client Selected</p>
                <small>Select a client from the Search tab to view their questions.</small>
            </div>
        );
    }
    const filteredClientQs = filteredClientQuestions();
    const filteredMaster = filteredMasterQuestions();
    return (
        <div className="cq-wrapper">
            <div className="cr-header">
                <div className="cr-header-top">
                    {currentClientName && <h2 className="cr-client-name">{currentClientName}'s Questions</h2>}
                </div>
            </div>

            {error && <div className="modal-error" style={{ textAlign: 'center' }}>{error}</div>}

            <div className="cq-question-filters">
                <input type="text" className="cr-q-modal-search-input" placeholder="Question" value={searchClientQ} onChange={e => setSearchClientQ(e.target.value)} />
                <input type="text" className="cr-q-modal-search-input" placeholder="Topic" value={searchClientTopic} onChange={e => setSearchClientTopic(e.target.value)} />
                <select className="cr-q-modal-search-input cr-q-modal-search-select" placeholder="Answered" value={searchClientAnswered} onChange={e => setSearchClientAnswered(e.target.value)}>
                    <option value="">All</option>
                    <option value="true">Answered</option>
                    <option value="false">Unanswered</option>
                </select>
                <input type="number" min={1900} className="cr-q-modal-search-input year-input" placeholder="Year" value={searchClientYear} onChange={e => setSearchClientYear(e.target.value)} style={{ maxWidth: '120px' }} />
            </div>
            <div className="cq-header">
                <button className="cq-btn-add" onClick={openAddModal}>Add Question</button>
            </div>

            {loading ? (
                <div className="cq-empty"><p>Loading questions...</p></div>
            ) : filteredClientQs.length === 0 ? (
                <div className="cq-empty">
                    <p>No questions found.</p>
                </div>
            ) : (
                <div className="cq-list">
                    {filteredClientQs.map(cq => (
                        <div key={cq.id} className="cq-row">
                            <div className="cq-row-main">
                                <div className="cq-meta">
                                    <span className="cq-tag cq-tag--topic">{cq.topic}</span>
                                    <span className="cq-tag cq-tag--year">{cq.year}</span>
                                </div>
                                <div className="cq-question-text">{cq.question}</div>
                                {cq.has_answer && (
                                    <div className="cq-answer-box">
                                        <div className="cq-answer-label">Answer</div>
                                        <div className="cq-answer-text">{cq.answer || <em>No text provided.</em>}</div>
                                    </div>
                                )}
                            </div>
                            <div className="cq-row-actions">
                                <button className="cq-modal-btn cq-modal-btn-edit" onClick={() => openEditModal(cq)}>Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">{isEditing ? 'Edit Question' : 'Add Question'}</h2>
                            <button className="modal-close" onClick={closeModal}>&#x2715;</button>
                        </div>
                        <div className="modal-body">
                            {!isEditing && (
                                <>
                                    <label className="modal-label">Search Questions</label>
                                    <div className="modal-search-row">
                                        <input className="cr-q-modal-search-input" placeholder="Question..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
                                        <input className="cr-q-modal-search-input" placeholder="Topic..." value={searchTopic} onChange={e => setSearchTopic(e.target.value)} />
                                    </div>
                                    <div className="modal-q-list">
                                        {masterLoading ? (
                                            <div style={{ padding: '1rem', color: '#888' }}>Loading master questions...</div>
                                        ) : filteredMaster.length === 0 ? (
                                            <div style={{ padding: '1rem', color: '#888' }}>No master questions found.</div>
                                        ) : (
                                            filteredMaster.map(q => (
                                                <div
                                                    key={q.id}
                                                    className={`modal-q-item ${selectedMasterQ?.id === q.id ? 'modal-q-item--selected' : ''}`}
                                                    onClick={() => { setSelectedMasterQ(q); setYearInput(''); }}
                                                >
                                                    <div className="modal-q-item-meta">{q.topic}</div>
                                                    <div className="modal-q-item-text">{q.question}</div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            )}

                            {!isEditing && selectedMasterQ && (
                                <div className="modal-year-input">
                                    <label className="modal-label">Year for this Question</label>
                                    <input
                                        className="cr-q-modal-search-input year-input"
                                        type="number"
                                        min={1900}
                                        value={yearInput}
                                        onChange={e => setYearInput(e.target.value)}
                                        placeholder="Year..."
                                        style={{ maxWidth: '120px' }}
                                    />
                                </div>
                            )}

                            {isEditing && selectedMasterQ && (
                                <>
                                    <label className="modal-label">Question</label>
                                    <div className="cq-answer-box" style={{ marginTop: 0 }}>
                                        <div className="modal-q-item-meta">{selectedMasterQ.topic}</div>
                                        <div className="modal-q-item-text" style={{ marginTop: '0.25rem' }}>{selectedMasterQ.question}</div>
                                    </div>
                                    <div className="modal-year-input">
                                        <label className="modal-label">Year</label>
                                        <input
                                            className="cr-q-modal-search-input year-input"
                                            type="number"
                                            min={1900}
                                            value={yearInput}
                                            onChange={e => setYearInput(e.target.value)}
                                            placeholder="Year..."
                                            style={{ maxWidth: '120px' }}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="modal-checkbox-row" style={{ marginTop: '1rem' }}>
                                <input
                                    type="checkbox"
                                    id="has_answer"
                                    checked={hasAnswer}
                                    onChange={e => setHasAnswer(e.target.checked)}
                                />
                                <label htmlFor="has_answer" className="modal-checkbox-label">Has Answer?</label>
                            </div>

                            {hasAnswer && (
                                <>
                                    <label className="modal-label">Answer Text</label>
                                    <textarea
                                        className="modal-textarea"
                                        rows={5}
                                        value={answerText}
                                        onChange={e => setAnswerText(e.target.value)}
                                        placeholder="Type answer here..."
                                    />
                                </>
                            )}

                            {saveError && <p className="modal-error">{saveError}</p>}
                        </div>
                        <div className="modal-footer">
                            {isEditing ? (
                                <button className="cq-modal-btn cq-modal-btn-delete" onClick={handleDelete} disabled={deleting || saving}>
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                            ) : <div></div>}

                            <div className="modal-footer-right">
                                <button className="cq-modal-btn cq-modal-btn-cancel" onClick={closeModal} disabled={saving || deleting}>Cancel</button>
                                <button className="cq-modal-btn cq-modal-btn-save" onClick={handleSave} disabled={saving || deleting || (!selectedMasterQ && !isEditing)}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Questions;
